import { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Target, Eye, Award } from 'lucide-react';

const cards = [
  { icon: Target, title: 'Our Mission', color: 'var(--teal)', text: 'To deliver reliable, high-impact technology solutions that empower businesses to grow securely and efficiently — every time, without exception.' },
  { icon: Eye, title: 'Our Vision', color: 'var(--amber)', text: 'To be the most trusted technology consulting partner for forward-thinking enterprises across India and beyond.' },
  { icon: Award, title: 'Our Standard', color: 'var(--teal-light)', text: 'Every engagement is measured against a simple standard: would we be proud to show this to the next client? If not, it is not done.' },
];

export default function About() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-80px' });

  return (
    <section id="about" ref={ref} style={{ padding: '100px 5%', background: 'var(--navy)', position: 'relative', overflow: 'hidden' }}>

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 1, background: 'linear-gradient(90deg, transparent, rgba(0,172,193,0.22), transparent)' }} />

      {/* Teal glow */}
      <div style={{ position: 'absolute', bottom: '5%', left: '10%', width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(0,172,193,0.08) 0%, transparent 70%)', zIndex: 0 }} />

      <div style={{ maxWidth: 1280, margin: '0 auto', position: 'relative', zIndex: 1 }}>

        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7 }}
          style={{ textAlign: 'center', marginBottom: 72 }}>
          <span style={{ fontFamily: 'Space Mono', fontSize: 11, letterSpacing: '0.25em', color: 'var(--teal)', textTransform: 'uppercase' }}>
            — About Us
          </span>
          <h2 style={{ fontSize: 'clamp(36px, 4vw, 56px)', fontWeight: 700, color: 'var(--text-main)', marginTop: 16, lineHeight: 1.1 }}>
            Who We Are
          </h2>
        </motion.div>

        {/* Story */}
        <motion.div initial={{ opacity: 0, y: 30 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.7, delay: 0.15 }}
          style={{
            background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
            borderRadius: 12, padding: 'clamp(32px, 5%, 60px)',
            marginBottom: 48, boxShadow: 'var(--shadow-soft)',
            position: 'relative', overflow: 'hidden',
          }}>
          {/* Accent corner */}
          <div style={{ position: 'absolute', top: 0, left: 0, width: 4, height: '100%', background: 'linear-gradient(to bottom, var(--teal), transparent)' }} />

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 60 }} className="about-story-grid">
            <div>
              <h3 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 28, color: 'var(--text-main)', marginBottom: 20 }}>
                Technology Consulting,<br /><span style={{ color: 'var(--teal-dark)' }}>Done Right.</span>
              </h3>
              <p style={{ color: 'var(--text-muted)', fontSize: 15.5, lineHeight: 1.85, fontWeight: 300 }}>
                KP Technologies is a specialised IT consulting firm dedicated to solving complex technology challenges for businesses of all sizes. We combine deep technical expertise with an understanding of real-world business constraints to deliver outcomes that matter.
              </p>
            </div>
            <div>
              <p style={{ color: 'var(--text-muted)', fontSize: 15.5, lineHeight: 1.85, fontWeight: 300, marginBottom: 20 }}>
                Our team comprises certified security professionals, experienced software engineers, network architects, and design specialists who share a single commitment: reliability. Not just in the solutions we build, but in how we communicate, how we manage timelines, and how we support clients after delivery.
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: 15.5, lineHeight: 1.85, fontWeight: 300 }}>
                Whether you need to pass an audit, build a product from scratch, secure your infrastructure, or rethink a broken process — KP Technologies brings the right people and the right methodology to the table.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Mission / Vision / Standard */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 24 }} className="about-cards-grid">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <motion.div key={c.title}
                initial={{ opacity: 0, y: 30 }}
                animate={inView ? { opacity: 1, y: 0 } : {}}
                transition={{ duration: 0.6, delay: 0.3 + i * 0.12 }}
                style={{
                  background: 'var(--navy-card)', border: '1px solid var(--navy-border)',
                  borderRadius: 8, padding: '32px 28px', textAlign: 'center',
                  transition: 'border-color 0.2s, box-shadow 0.2s',
                  boxShadow: '0 18px 44px rgba(15,23,42,0.05)',
                }}
                onMouseEnter={e => e.currentTarget.style.borderColor = `${c.color}44`}
                onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--navy-border)'}>
                <div style={{ width: 56, height: 56, borderRadius: '50%', background: `${c.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
                  <Icon size={24} color={c.color} />
                </div>
                <h4 style={{ fontFamily: 'Rajdhani', fontWeight: 700, fontSize: 20, color: 'var(--text-main)', marginBottom: 14 }}>{c.title}</h4>
                <p style={{ color: 'var(--text-muted)', fontSize: 14.5, lineHeight: 1.75, fontWeight: 300 }}>{c.text}</p>
              </motion.div>
            );
          })}
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .about-story-grid { grid-template-columns: 1fr !important; gap: 24px !important; }
          .about-cards-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </section>
  );
}
